import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { FloorId, FloorPlanId, ProjectId, TowerId } from '../shared/ids';
import { STRUCTURE_ERROR, STRUCTURE_ERROR_MESSAGE } from './errors';

/** Quantos pavimentos a criação em sequência aceita de uma vez. */
export const MAX_FLOOR_RANGE = 100;

/**
 * Pavimento.
 *
 * **Existe uma única vez dentro da torre** (RN-003). Um pavimento recebe várias
 * disciplinas através de `Activity` — nunca se cria "Térreo" três vezes, um para
 * Fundação, outro para Elétrica e outro para Alvenaria. Essa é a normalização que o
 * requisito exige e a razão de ser da entidade `Activity`.
 */
export interface Floor {
  readonly id: FloorId;
  readonly projectId: ProjectId;
  readonly towerId: TowerId;
  readonly name: string;
  readonly code?: string;
  /**
   * Nível real na edificação: negativo para subsolo, 0 para térreo, positivo acima.
   *
   * É por ele que a árvore ordena (RN-005). Ordenar por nome colocaria "10º Pavimento"
   * antes de "2º Pavimento" — defeito clássico e silencioso.
   */
  readonly level: number;
  /**
   * O **desenho** que este pavimento usa — o pavimento tipo da Fase 14.
   *
   * Ausente é o estado de toda obra que existia antes do quantitativo, e continua sendo o
   * de quem só usa cronograma: o pavimento é real, tem atividade e prazo, e não precisa
   * de desenho para isso.
   *
   * É aqui que o prédio real encontra a tipologia. O quantitativo mora no desenho, e o
   * total é ele vezes os pavimentos que o apontam (RN-167) — sem este vínculo, o
   * multiplicador teria de ser digitado, que foi exatamente o defeito que a fase passou
   * a evitar.
   */
  readonly floorPlanId?: FloorPlanId;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateFloorInput {
  readonly id: FloorId;
  readonly projectId: ProjectId;
  readonly towerId: TowerId;
  readonly name: string;
  readonly code?: string;
  readonly level: number;
  readonly now: string;
}

export function createFloor(input: CreateFloorInput): Result<Floor, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(domainError(STRUCTURE_ERROR.FLOOR_NOT_FOUND, 'O nome do pavimento é obrigatório.'));
  }
  if (!Number.isInteger(input.level)) {
    return err(
      domainError(STRUCTURE_ERROR.INVALID_LEVEL_RANGE, 'O nível deve ser um número inteiro.'),
    );
  }
  return ok({
    id: input.id,
    projectId: input.projectId,
    towerId: input.towerId,
    name,
    level: input.level,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.code === undefined ? {} : { code: input.code }),
  });
}

/** Chave natural do pavimento dentro da torre (RN-003). */
export function floorNaturalKey(towerId: TowerId, name: string): string {
  return `${towerId}|${normalizeName(name)}`;
}

export function floorNameConflict(name: string, existing: readonly Floor[]): boolean {
  const normalized = normalizeName(name);
  return existing.some((floor) => normalizeName(floor.name) === normalized);
}

export function floorNameTakenError(name: string): DomainError {
  return domainError(
    STRUCTURE_ERROR.FLOOR_NAME_TAKEN,
    STRUCTURE_ERROR_MESSAGE[STRUCTURE_ERROR.FLOOR_NAME_TAKEN]!,
    { name },
  );
}

/**
 * Aponta o pavimento para um desenho, ou desfaz o vínculo.
 *
 * `undefined` desliga: o pavimento continua existindo, com atividade e prazo, e some do
 * quantitativo. É o caminho de quem ligou o andar ao desenho errado — e o único, porque
 * apagar o pavimento levaria junto o cronograma dele.
 *
 * Devolve o mesmo objeto quando nada muda, para não gravar `updatedAt` novo nem produzir
 * linha de auditoria de uma edição que não houve.
 */
export function linkFloorToPlan(
  floor: Floor,
  floorPlanId: FloorPlanId | undefined,
  now: string,
): Floor {
  if (floor.floorPlanId === floorPlanId) return floor;
  const { floorPlanId: _atual, ...resto } = floor;
  return {
    ...resto,
    ...(floorPlanId === undefined ? {} : { floorPlanId }),
    updatedAt: now,
  };
}

/**
 * Ordenação por nível, do mais baixo para o mais alto.
 *
 * Subsolo → Térreo → 1º → 2º → … → 10º. Nome só como desempate, para pavimentos que
 * dividem o mesmo nível (ex.: mezanino).
 */
export function compareFloors(a: Floor, b: Floor): number {
  return a.level - b.level || normalizeName(a.name).localeCompare(normalizeName(b.name), 'pt-BR');
}

// ── Criação em sequência ────────────────────────────────────────────────────────

export interface FloorRangeSpec {
  readonly fromLevel: number;
  readonly toLevel: number;
  /** Modelo do nome com `{n}` no lugar do nível — ex.: `"{n}º Pavimento"`. */
  readonly namePattern: string;
  /**
   * O modelo dos níveis **negativos**, com `{n}` no lugar da profundidade — `"G{n}"` dá
   * `G2`, `G1`. Ausente = `{n}º Subsolo`, que era o nome imposto antes (RN-239).
   */
  readonly belowGroundPattern?: string | undefined;
}

export interface PlannedFloor {
  readonly name: string;
  readonly level: number;
}

/**
 * Calcula os pavimentos de uma criação em sequência.
 *
 * Função pura: devolve o que **seria** criado, para que a interface possa mostrar a
 * pré-visualização antes de gravar qualquer coisa. O térreo é resolvido por convenção, em
 * vez de sair "0º Pavimento"; os negativos seguem o modelo deles, e não mais um nome fixo.
 */
export function planFloorRange(spec: FloorRangeSpec): Result<PlannedFloor[], DomainError> {
  if (!Number.isInteger(spec.fromLevel) || !Number.isInteger(spec.toLevel)) {
    return err(
      domainError(STRUCTURE_ERROR.INVALID_LEVEL_RANGE, 'Os níveis devem ser números inteiros.'),
    );
  }
  if (spec.fromLevel > spec.toLevel) {
    return err(
      domainError(
        STRUCTURE_ERROR.INVALID_LEVEL_RANGE,
        STRUCTURE_ERROR_MESSAGE[STRUCTURE_ERROR.INVALID_LEVEL_RANGE]!,
      ),
    );
  }

  const total = spec.toLevel - spec.fromLevel + 1;
  if (total > MAX_FLOOR_RANGE) {
    return err(
      domainError(
        STRUCTURE_ERROR.RANGE_TOO_LARGE,
        STRUCTURE_ERROR_MESSAGE[STRUCTURE_ERROR.RANGE_TOO_LARGE]!,
        { total },
      ),
    );
  }

  const floors: PlannedFloor[] = [];
  for (let level = spec.fromLevel; level <= spec.toLevel; level += 1) {
    floors.push({
      level,
      name: floorNameForLevel(level, spec.namePattern, spec.belowGroundPattern),
    });
  }
  return ok(floors);
}

/**
 * `0 → "Térreo"` · `-1 → "1º Subsolo"` (ou `"G1"`) · `3 → "3º Pavimento"` — conforme os
 * modelos.
 *
 * O negativo tinha nome fixo, e era o único jeito de criar pavimento pela tela: a obra que
 * chama o andar de baixo de "Fundação" ou de "G1" não tinha como. O nível continua
 * negativo — é ele que ordena (RN-005) —, e o nome passa a ser da obra (RN-238).
 */
export function floorNameForLevel(
  level: number,
  namePattern: string,
  belowGroundPattern: string = DEFAULT_BELOW_GROUND_NAME_PATTERN,
): string {
  if (level === 0) return 'Térreo';
  if (level < 0) return belowGroundPattern.replace('{n}', String(Math.abs(level)));
  return namePattern.replace('{n}', String(level));
}

export const DEFAULT_FLOOR_NAME_PATTERN = '{n}º Pavimento';

/** O modelo dos níveis negativos quando a sequência não diz outro (RN-239). */
export const DEFAULT_BELOW_GROUND_NAME_PATTERN = '{n}º Subsolo';
